import {
  ArrowRightIcon,
  CustomReward,
  CustomRewardCurrency,
  EditIcon,
  FilterIcon,
  FlexBox,
  JoonColorExpanded,
  JoonUIColor,
  SPACING,
  Switch,
  TextButton,
  Typography,
} from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

import FilterItem from "./FilterItem"
import RewardChildRedeemCard from "./RewardChildRedeemCard"
import { RewardLogRow, RewardLogTableLabel } from "./RewardLogRow"
import { useScreenTimeControlsModalStore } from "./ScreenTimeControlsModal"
import UserBadge from "../../../components/badges/userBadge/UserBadge"
import useGetStarted from "../../../components/getStarted/useGetStarted"
import PageContentWrapper from "../../../components/pageContentWrapper/PageContentWrapper"
import PageWrapper from "../../../components/pageWrapper/PageWrapper"
import {
  GRADIENTS,
  ScreenTimeControlsAnalyticsStep,
  ScreenTimeControlsModalStep,
} from "../../../constants"
import useMobile from "../../../hooks/useMobile"
import { useEditRewardMutation } from "../../../networking/rewards/mutations"
import {
  useCustomRewardLogsQuery,
  useCustomRewardLogsStatsQuery,
  useCustomRewardsQuery,
} from "../../../networking/rewards/queries"
import { ScreenTimeControlsModalType } from "../../../types/rewards"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../../util/analytics"
import { requireAuth } from "../../../util/auth"
import { useRouter } from "../../../util/router"
import { getScreenTimeConfigurations } from "../../../util/util"
import { QuickActionMobileButton } from "../../me/QuickActionsSection"
import useAddEditRewardModalStore from "../hooks/useAddEditRewardModalStore"
import { useFilterRewardLogsModalStore } from "../hooks/useFilterRewardLogsModalStore"
import usePurchaseRewardModalStore from "../hooks/usePurchaseRewardModalStore"

const RewardLogDetailsPage = () => {
  const { onOpen: openAddEditRewardModal } = useAddEditRewardModalStore()
  const {
    onOpen: openFilterRewardLogsModal,
    selectedUser,
    setSelectedUser,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
  } = useFilterRewardLogsModalStore()
  const { onOpen: onOpenPurchaseRewardModal } = usePurchaseRewardModalStore()
  const { onOpen: onOpenScreenTimeControlsModal } =
    useScreenTimeControlsModalStore()
  const router = useRouter()
  const { rewardId } = router.params
  const { data: customRewards } = useCustomRewardsQuery()
  const { data: customRewardLogs, isLoading: isCustomRewardLogsLoading } =
    useCustomRewardLogsQuery({ rewardId: Number(rewardId) })
  const { data: customRewardLogsStats } = useCustomRewardLogsStatsQuery({
    rewardId: Number(rewardId),
  })
  const rewardDetails: CustomReward | null =
    customRewards?.find((reward) => reward.id === Number(rewardId)) ||
    customRewardLogs?.find(
      (rewardLog) => rewardLog.reward_id === Number(rewardId)
    )?.reward ||
    null
  const { numLinkedDevices } = getScreenTimeConfigurations(rewardDetails)
  const editRewardMutation = useEditRewardMutation()
  const isMobile = useMobile()
  const { t } = useTranslation()

  const { hasCompletedAllMissions } = useGetStarted()

  const hasFilters = selectedUser || startDate || endDate
  const isMinuteReward = rewardDetails?.currency === CustomRewardCurrency.MINUTE
  const isDollarReward = rewardDetails?.currency === CustomRewardCurrency.DOLLAR
  const isItemReward = rewardDetails?.currency === CustomRewardCurrency.ITEM

  const onFilterClick = () => {
    if (!customRewardLogs?.length) return
    openFilterRewardLogsModal(Number(rewardId))
  }

  const onClearClick = () => {
    setSelectedUser(null)
    setStartDate(null)
    setEndDate(null)
  }

  const onScreenTimeControlsClick = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.REWARD_DETAILS_SETUP_SCREENTIME)
    onOpenScreenTimeControlsModal({
      step:
        numLinkedDevices > 0
          ? ScreenTimeControlsModalStep.SETUP_PASSCODE
          : ScreenTimeControlsModalStep.INTRO,
      modalType: ScreenTimeControlsModalType.REWARDS_DETAILS,
      rewardId: Number(rewardId),
    })
    if (numLinkedDevices > 0)
      trackAnalyticEvent(ANALYTIC_EVENTS.SCREENTIME_INSTRUCTIONS, {
        step: ScreenTimeControlsAnalyticsStep.SETUP_PASSCODE,
      })
  }

  return (
    <PageWrapper style={{ background: GRADIENTS.gradient100 }}>
      <PageContentWrapper mobileGutter>
        <FlexBox
          direction="row"
          justify="space-between"
          align="center"
          wrap={false}
          style={{ paddingTop: SPACING.space4, paddingBottom: SPACING.space4 }}
        >
          <TextButton
            onClick={() => {
              router.push("/rewards")
              onClearClick()
            }}
            style={{ transform: "rotate(180deg)", padding: SPACING.space1 }}
          >
            <ArrowRightIcon size={20} color={JoonUIColor.icon.neutral} />
          </TextButton>
          <Typography
            variant="h3"
            textAlign="center"
            color={JoonColorExpanded.indigo700}
            style={{ wordBreak: "break-word" }}
          >
            {!rewardDetails && isCustomRewardLogsLoading ? (
              <Typography variant="bodyXSmall">
                {t("REWARD_LOADING")}
              </Typography>
            ) : rewardDetails ? (
              `${rewardDetails?.emoji} ${rewardDetails?.title}`
            ) : (
              <Typography variant="bodyXSmall">
                {t("REWARD_NO_REWARD_FOUND")}
              </Typography>
            )}
          </Typography>
          {rewardDetails && !rewardDetails?.date_deleted ? (
            <TextButton onClick={() => openAddEditRewardModal(rewardDetails)}>
              <EditIcon size={16} color={JoonUIColor.icon.neutral} />
            </TextButton>
          ) : (
            <TextButton
              onClick={() => {}}
              style={{ pointerEvents: "none", opacity: 0 }}
            >
              <div style={{ width: 16, height: 16 }}></div>
            </TextButton>
          )}
        </FlexBox>

        <FlexBox direction="column" gap={SPACING.space6} wrap={false}>
          <FlexBox
            direction="column"
            justify="flex-start"
            align="flex-start"
            gap={SPACING.space1}
            wrap={false}
            style={{
              border: `1px solid ${JoonUIColor.border.default}`,
              borderRadius: SPACING.space2,
              padding: SPACING.space3,
              background: JoonUIColor.background.primaryNeutral,
            }}
          >
            {rewardDetails?.date_deleted ? (
              <FlexBox direction="column" gap={SPACING.space2}>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.secondary}
                  style={{
                    background: JoonColorExpanded.neutral200,
                    padding: `${SPACING.space0} ${SPACING.space2}`,
                    borderRadius: SPACING.space2,
                  }}
                >
                  {t("REWARD_DELETED_ON", {
                    date: dayjs(rewardDetails?.date_deleted).format("MMM D"),
                  })}
                </Typography>
                <Typography variant="bodyXSmall">
                  {isMinuteReward
                    ? t("REWARD_DELETED_NOTICE_MINUTE")
                    : isDollarReward
                      ? t("REWARD_DELETED_NOTICE_DOLLAR")
                      : t("REWARD_DELETED_NOTICE_ITEM")}
                </Typography>
              </FlexBox>
            ) : (
              rewardDetails && (
                <FlexBox
                  direction="row"
                  justify="space-between"
                  align="center"
                  wrap={false}
                >
                  <Typography variant="bodySmallBold">
                    {t("REWARD_ALLOW_PURCHASE")}
                  </Typography>
                  <Switch
                    checked={rewardDetails.purchasable_from_joonity}
                    name="availableForPurchase"
                    onChange={() =>
                      editRewardMutation.mutate({
                        rewardId: rewardDetails.id,
                        rewardInfo: {
                          title: rewardDetails.title,
                          cost: rewardDetails.cost,
                          emoji: rewardDetails.emoji,
                          user_ids: rewardDetails.assignments.map(
                            (assignment) => assignment.user_id
                          ),
                          purchasable_from_joonity:
                            !rewardDetails.purchasable_from_joonity,
                        },
                      })
                    }
                  />
                </FlexBox>
              )
            )}

            <FlexBox
              direction="column"
              gap={SPACING.space1}
              style={{ marginBottom: SPACING.space1 }}
            >
              <Typography
                variant="bodyXSmallBold"
                color={JoonUIColor.text.secondary}
              >
                {isItemReward ? t("REWARD_COST") : t("REWARD_EXCHANGE_RATE")}
              </Typography>
              <FlexBox
                gap={SPACING.space1}
                direction="row"
                wrap={false}
                align="center"
              >
                <img
                  style={{ height: SPACING.space3 }}
                  src="/images/icons/coin-icon.png"
                  alt="coin"
                />
                <Typography variant="bodySmall">
                  {rewardDetails?.cost}
                  {isMinuteReward
                    ? t("REWARD_PER_MINUTE")
                    : isDollarReward
                      ? t("REWARD_PER_DOLLAR")
                      : ""}
                </Typography>
              </FlexBox>
            </FlexBox>

            {!rewardDetails?.date_deleted && rewardDetails?.assignments && (
              <FlexBox direction="column" gap={SPACING.space1}>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.secondary}
                >
                  {t("REWARD_AVAILABLE_FOR")}
                </Typography>
                <FlexBox direction="row" gap={SPACING.space1}>
                  {rewardDetails.assignments.length === 0 ? (
                    <Typography variant="bodyXSmall">-</Typography>
                  ) : (
                    rewardDetails.assignments.map((assignment, i) => (
                      <UserBadge userId={assignment.user_id} key={i} />
                    ))
                  )}
                </FlexBox>
              </FlexBox>
            )}
          </FlexBox>

          <FlexBox direction="column" gap={SPACING.space2}>
            <Typography variant="bodyBold">
              {t("REWARD_CURRENT_BALANCE")}
            </Typography>
            {rewardDetails?.assignments.map((assignment) => (
              <RewardChildRedeemCard
                childId={assignment.user_id}
                key={assignment.user_id}
                reward={rewardDetails}
              />
            ))}
          </FlexBox>

          <FlexBox direction="column" gap={SPACING.space2}>
            <Typography variant="bodyBold">{t("REWARD_ANALYTICS")}</Typography>

            <FlexBox direction="row" justify="space-between" wrap={false}>
              <FlexBox direction="column" gap={SPACING.space1}>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.secondary}
                >
                  {t("REWARD_TOTAL_PURCHASED_ALL")}
                </Typography>
                <Typography
                  variant="bodySmallBold"
                  color={JoonUIColor.text.primary}
                >
                  {customRewardLogsStats?.purchased_all_time}
                </Typography>
              </FlexBox>
              <FlexBox direction="column" gap={SPACING.space1}>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.secondary}
                >
                  {t("REWARD_TOTAL_PURCHASED_WEEK")}
                </Typography>
                <Typography
                  variant="bodySmallBold"
                  color={JoonUIColor.text.primary}
                >
                  {customRewardLogsStats?.purchased_this_week}
                </Typography>
              </FlexBox>
            </FlexBox>

            <FlexBox direction="row" justify="space-between" wrap={false}>
              <FlexBox direction="column" gap={SPACING.space1}>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.secondary}
                >
                  {isMinuteReward
                    ? t("REWARD_TOTAL_APPROVED_ALL")
                    : t("REWARD_TOTAL_USED_ALL")}
                </Typography>
                <Typography
                  variant="bodySmallBold"
                  color={JoonUIColor.text.primary}
                >
                  {customRewardLogsStats?.redeemed_all_time}
                </Typography>
              </FlexBox>
              <FlexBox direction="column" gap={SPACING.space1}>
                <Typography
                  variant="bodyXSmallBold"
                  color={JoonUIColor.text.secondary}
                >
                  {isMinuteReward
                    ? t("REWARD_TOTAL_APPROVED_WEEK")
                    : t("REWARD_TOTAL_USED_WEEK")}
                </Typography>
                <Typography
                  variant="bodySmallBold"
                  color={JoonUIColor.text.primary}
                >
                  {customRewardLogsStats?.redeemed_this_week}
                </Typography>
              </FlexBox>
            </FlexBox>
          </FlexBox>

          <FlexBox
            direction="column"
            gap={SPACING.space2}
            style={{ marginBottom: SPACING.space14 }}
          >
            <FlexBox direction="column" gap={SPACING.space1}>
              <FlexBox
                direction="row"
                justify="space-between"
                align="center"
                wrap={false}
              >
                <TextButton
                  onClick={() => router.push("/rewards/transactions")}
                  style={{
                    padding: 0,
                  }}
                >
                  <Typography variant="bodyBold">
                    {t("REWARD_TRANSACTION_HISTORY")}
                  </Typography>
                </TextButton>
                <TextButton onClick={onFilterClick}>
                  <FilterIcon
                    size={16}
                    color={
                      hasFilters
                        ? JoonUIColor.icon.accent
                        : JoonUIColor.icon.neutral
                    }
                  />
                </TextButton>
              </FlexBox>
              {hasFilters && (
                <FlexBox
                  direction="row"
                  justify="space-between"
                  align="flex-end"
                  wrap={false}
                >
                  <FlexBox direction="row" gap={SPACING.space1}>
                    {selectedUser && <FilterItem user={selectedUser} />}
                    {startDate && endDate ? (
                      <FilterItem startDate={startDate} endDate={endDate} />
                    ) : startDate ? (
                      <FilterItem startDate={startDate} />
                    ) : endDate ? (
                      <FilterItem endDate={endDate} />
                    ) : null}
                  </FlexBox>
                  <TextButton onClick={onClearClick} style={{ padding: 0 }}>
                    <Typography
                      variant="bodySmall"
                      color={JoonUIColor.text.primaryAccent}
                    >
                      {t("REWARD_CLEAR")}
                    </Typography>
                  </TextButton>
                </FlexBox>
              )}
            </FlexBox>

            <RewardLogTableLabel />
            <hr />
            {isCustomRewardLogsLoading ? (
              <Typography variant="bodyXSmall">
                {t("REWARD_LOADING")}
              </Typography>
            ) : customRewardLogs?.length ? (
              customRewardLogs?.map((log) => (
                <RewardLogRow log={log} key={log.id} />
              ))
            ) : (
              <Typography variant="bodyXSmall">
                {t("REWARD_NO_LOGS")}
              </Typography>
            )}
          </FlexBox>
        </FlexBox>
      </PageContentWrapper>
      {rewardDetails && !rewardDetails.date_deleted && (
        <FlexBox
          direction="row"
          justify="center"
          align="center"
          gap={SPACING.space2}
          wrap={false}
          style={{
            position: "fixed",
            zIndex: 500,
            width: "min(600px, 100%)",
            bottom: isMobile
              ? hasCompletedAllMissions
                ? "78px"
                : "125px"
              : "20px",
            borderRadius: SPACING.space2,
            background: "rgba(13, 5, 105, 0.12)",
            padding: SPACING.space2,
          }}
          fullWidth
        >
          <QuickActionMobileButton
            text={t("REWARD_PURCHASE")}
            onClick={() =>
              rewardDetails && onOpenPurchaseRewardModal(rewardDetails)
            }
          />
          {isMinuteReward && (
            <div style={{ width: "68%" }}>
              <QuickActionMobileButton
                text={
                  numLinkedDevices > 0
                    ? t("REWARD_ST_CONTROLS_HELP")
                    : t("REWARD_ST_CONTROLS_SETUP")
                }
                onClick={onScreenTimeControlsClick}
              />
            </div>
          )}
        </FlexBox>
      )}
    </PageWrapper>
  )
}

const RewardLogDetailsPageWithAuth = requireAuth(RewardLogDetailsPage)
export default RewardLogDetailsPageWithAuth
